<template>
  <div class="page-wrapper" style="font-family: Pretendard;">
    <div class="background-page">
      <div class="container purchase-container">
        <div v-if="state === 2" class="status-body">
          <div class="status-title">Gift card</div>
          <div class="status-text">{{message}}</div>
        </div>

        <div v-else-if="pendingPayment" class="status-body">
          <div class="status-title">Waiting for payment</div>
          <div class="status-text">Payment confirmation is still processing.</div>
        </div>

        <div v-else-if="inviteMode && !cardView" class="status-body">
          <div class="status-text">Loading...</div>
        </div>

        <div v-else-if="inviteMode && inviteExpired" class="status-body">
          <div class="status-title">Invitation expired</div>
          <div class="status-text">This invitation has already been used.</div>
        </div>

        <div v-else-if="inviteMode" class="register-body">
          <div class="page-kicker">You've been sent a gift card</div>
          <div class="virtual-card" v-if="merchant">
            <div class="virtual-card-top">
              <div class="virtual-card-merchant-block">
                <div class="virtual-card-merchant">{{merchant.title}}</div>
                <div class="virtual-card-address" v-if="merchantAddress">{{merchantAddress}}</div>
              </div>
              <img class="virtual-card-icon" src="/static/img/gift_card_icon.png" alt=""/>
            </div>
            <div class="virtual-card-bottom">
              <div class="virtual-card-label">GIFT CARD</div>
              <div class="virtual-card-amount">£{{displayBalance}}</div>
            </div>
          </div>
          <div class="section-copy">Enter your email to accept. You can also send a message to the person who bought it.</div>
          <div class="form-block">
            Email <span class="mandatory-text">*</span>
            <input type="email" class="form-control" v-model="email" placeholder="email@example.com"/>
          </div>
          <div class="form-block">
            Name <span class="optional-text">(optional)</span>
            <input type="text" class="form-control" v-model="name" placeholder="Name"/>
          </div>
          <div class="form-block">
            Message to sender <span class="optional-text">(optional)</span>
            <textarea class="form-control gift-message" v-model="inviteMessage" rows="3" maxlength="500"
                      placeholder="Say thank you (optional)"></textarea>
          </div>
          <div class="form-error" v-if="inviteError">{{inviteError}}</div>
          <button class="primary-btn" @click="acceptInvite">Accept gift card</button>
        </div>

        <div v-else-if="giftPurchaseComplete" class="status-body">
          <div class="status-title">Gift sent</div>
          <div class="status-text">Thank you. We've emailed the gift card to <b>{{cardView.email}}</b>.</div>
        </div>

        <div v-else-if="!cardView" class="status-body">
          <div class="status-text">Loading...</div>
        </div>

        <div v-else class="register-body">
          <div class="page-kicker" v-if="purchaseSuccess">Thank you for your purchase</div>
          <div class="virtual-card" v-if="merchant">
            <div class="virtual-card-top">
              <div class="virtual-card-merchant-block">
                <div class="virtual-card-merchant">{{merchant.title}}</div>
                <div class="virtual-card-address" v-if="merchantAddress">{{merchantAddress}}</div>
              </div>
              <img class="virtual-card-icon" src="/static/img/gift_card_icon.png" alt=""/>
            </div>
            <div class="virtual-card-bottom">
              <div class="virtual-card-label">GIFT CARD</div>
              <div class="virtual-card-amount">£{{displayBalance}}</div>
            </div>
          </div>

          <div class="qr-panel" v-if="cardView.qrCode">
            <img :src="cardView.qrCode" alt="Gift card QR code"/>
            <div class="qr-hint">Show this QR code at the till</div>
            <div class="qr-code-text" v-if="cardCode">{{cardCode}}</div>
          </div>

          <div v-if="showShare">
            <InviteShareButtons :url="inviteUrl" item-label="gift card"
                                 :merchant-title="merchant && merchant.title ? merchant.title : ''"/>
          </div>

          <div v-if="showRegister">
            <div class="section-title">Register your gift card</div>
            <div class="section-copy">Register with your email to keep this gift card on your phone.</div>
            <div class="form-block">
              Email <span class="mandatory-text">*</span>
              <input type="email" class="form-control" v-model="email" placeholder="email@example.com"/>
            </div>
            <div class="form-block">
              Name <span class="optional-text">(optional)</span>
              <input type="text" class="form-control" v-model="name" placeholder="Name"/>
            </div>
            <div class="form-block">
              Phone <span class="optional-text">(optional)</span>
              <input type="tel" class="form-control" v-model="phone" placeholder="Phone"/>
            </div>
            <button class="primary-btn" @click="submit">Register my gift card</button>
          </div>

          <div v-if="showCard">
            <a v-if="showApple && cardView.status >= 0" :href="walletUrl" class="wallet-btn">
              <i class="fab fa-apple"></i>
              Add to Apple Wallet
            </a>
            <a v-if="showGoogle && cardView.status >= 0" :href="googleWalletUrl" class="wallet-btn">
              <i class="fab fa-google"></i>
              Add to Google Wallet
            </a>
            <div class="registered-to" v-if="cardView.email">Registered to {{cardView.email}}</div>
            <div class="share-block" v-if="canSendGift">
              <InviteShareButtons :url="inviteUrl" item-label="gift card"
                                   :merchant-title="merchant && merchant.title ? merchant.title : ''"/>
            </div>
            <div class="expiry-note">
              <span v-if="cardView.expiresAt > 0">Expires {{dateString(cardView.expiresAt)}}</span>
              <span v-else>No expiry date</span>
            </div>
          </div>
        </div>

        <div class="page-footer">
          <div class="footer-merchant" v-if="merchant">
            <strong>{{merchant.title}}</strong>
            <div v-if="merchantAddress">{{merchantAddress}}</div>
          </div>
          <div class="privacy-note">
            Your email address is only used for this gift card and will not be shared with any third party.
          </div>
          <div class="powered-by">
            Powered by <strong>Borium Studio Limited</strong>
          </div>
        </div>
      </div>
    </div>
    <div class="page-gif-loader" v-if="pendingPayment || (!cardView && state !== 2 && !message)">
      <UtensilLoader/>
    </div>
  </div>
</template>

<script>
import {acceptGiftCardInvite, emailTokenFromQueryOrUrl, getGiftCard, getGiftCardGoogleWalletUrl, getGiftCardWalletUrl, registerGiftCard} from "../api/borium";
import InviteShareButtons from "./InviteShareButtons.vue";
import UtensilLoader from "./UtensilLoader.vue";

export default {
  name: 'GiftCardRegister',
  components: { InviteShareButtons, UtensilLoader },
  data() {
    return {
      code: '',
      email: '',
      name: '',
      phone: '',
      inviteMessage: '',
      inviteError: '',
      inviteAccepted: false,
      state: 0,
      message: '',
      cardView: false,
      merchant: false,
      pendingPayment: false,
      pollTimer: null,
      viewportNudged: false,
    }
  },
  computed: {
    emailToken: function() {
      return emailTokenFromQueryOrUrl(this.$route.query.e, this.cardView && this.cardView.registerUrl);
    },
    walletUrl: function() {
      return getGiftCardWalletUrl(this.code, this.emailToken);
    },
    googleWalletUrl: function() {
      return getGiftCardGoogleWalletUrl(this.code, this.emailToken);
    },
    showApple: function() {
      return this.isApple || !this.isGoogle;
    },
    showGoogle: function() {
      return this.isGoogle || !this.isApple;
    },
    inviteMode: function() {
      return !this.inviteAccepted && this.$route.path.indexOf('/giftcard/invite') === 0;
    },
    purchaseSuccess: function() {
      return this.$route.query.result === 'success';
    },
    giftPurchaseComplete: function() {
      return this.purchaseSuccess
        && this.cardView
        && this.cardView.sentAsGift;
    },
    inviteExpired: function() {
      return this.cardView && !this.cardView.inviteOpen;
    },
    showShare: function() {
      return this.cardView && this.cardView.inviteOpen && !this.cardView.email && this.canSendGift;
    },
    showCard: function() {
      return this.cardView && this.cardView.status >= 0 && this.cardView.email;
    },
    canSendGift: function() {
      if (!this.cardView || this.cardView.status < 0) {
        return false;
      }
      if (this.cardView.used > 0 || this.cardView.balance <= 0) {
        return false;
      }
      return true;
    },
    showRegister: function() {
      return this.cardView && !this.cardView.email && !this.cardView.inviteOpen;
    },
    inviteUrl: function() {
      return window.location.origin + '/giftcard/invite?code=' + encodeURIComponent(this.code);
    },
    isApple: function() {
      let ua = navigator.userAgent || '';
      return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);
    },
    isGoogle: function() {
      return /Android/.test(navigator.userAgent || '');
    },
    merchantAddress: function() {
      if (!this.merchant) {
        return '';
      }
      let parts = [];
      if (this.merchant.address) {
        parts.push(String(this.merchant.address).replace(/\n/g, ', '));
      }
      if (this.merchant.city) parts.push(this.merchant.city);
      if (this.merchant.postCode) parts.push(this.merchant.postCode);
      return parts.join(', ');
    },
    displayBalance: function() {
      if (!this.cardView) {
        return '0.00';
      }
      return (this.cardView.balance / 100).toFixed(2);
    },
    cardCode: function() {
      if (this.cardView && this.cardView.code) {
        return this.cardView.code;
      }
      return this.code || '';
    }
  },
  mounted() {
    this.initialise();
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
  },
  methods: {
    initialise: function() {
      if (this.$route.query.result === 'failed') {
        this.message = 'Payment was cancelled or failed.';
        this.state = 2;
        return;
      }
      if (this.$route.query.code !== undefined) {
        this.code = this.$route.query.code;
      }
      if (!this.code) {
        this.message = 'No gift card code provided';
        this.state = 2;
        return;
      }
      this.loadCard(true);
    },
    nudgeViewport: function() {
      // Workaround for Chrome on iOS 26 laying the page out under the status
      // bar / toolbar when opened fresh from another app (e.g. the Gmail app).
      // Any browser-level re-layout repairs it (which is why tapping the Apple
      // Wallet link fixed the page), so we force one after content renders:
      // toggle the wrapper height to invalidate layout, flush a reflow, then
      // nudge the scroll position so the browser re-measures its viewport.
      if (this.viewportNudged) {
        return;
      }
      this.viewportNudged = true;
      let self = this;
      let nudge = function() {
        let el = self.$el;
        if (!el || !el.style) {
          return;
        }
        let original = el.style.minHeight;
        el.style.minHeight = (window.innerHeight + 1) + 'px';
        void el.offsetHeight; // force a synchronous reflow
        window.requestAnimationFrame(function() {
          el.style.minHeight = original;
          void el.offsetHeight;
          el.scrollTop = 1;
          window.scrollTo(0, 1);
          window.requestAnimationFrame(function() {
            el.scrollTop = 0;
            window.scrollTo(0, 0);
          });
        });
      };
      this.$nextTick(function() {
        nudge();
        // Second pass in case the browser toolbar had not settled yet at the
        // time of the first nudge (common right after an app-to-app open).
        setTimeout(nudge, 500);
      });
    },
    loadCard: function(startPoll) {
      let self = this;
      getGiftCard(this.code, this.$route.query.e, this.inviteMode, this.purchaseSuccess)
        .then(function(data) {
          if (data.success === true) {
            if (data.merchant) {
              self.merchant = data.merchant;
            }
            if (data.item.status === -1) {
              self.pendingPayment = true;
              self.nudgeViewport();
              if (startPoll && !self.pollTimer) {
                self.pollTimer = setInterval(function() {
                  self.loadCard(false);
                }, 2500);
              }
              return;
            }
            self.pendingPayment = false;
            if (self.pollTimer) {
              clearInterval(self.pollTimer);
              self.pollTimer = null;
            }
            self.cardView = data.item;
            self.keepEmailTokenInRoute(data.item);
            self.nudgeViewport();
          } else {
            self.message = data.message;
            self.state = 2;
          }
        })
        .catch(function() {
          self.message = 'Could not load this gift card';
          self.state = 2;
        });
    },
    keepEmailTokenInRoute: function(item, path) {
      let token = emailTokenFromQueryOrUrl(this.$route.query.e, item && item.registerUrl);
      if (!token) {
        return;
      }
      if (this.$route.query.e === token && !path) {
        return;
      }
      let query = Object.assign({}, this.$route.query);
      query.code = this.code;
      query.e = token;
      this.$router.replace({ path: path || this.$route.path, query: query }).catch(function() {});
    },
    submit: function() {
      if (!this.email) {
        return;
      }
      let self = this;
      registerGiftCard({
        code: this.code,
        email: this.email.trim().toLowerCase(),
        name: this.name,
        phone: this.phone,
        e: this.emailToken,
      })
        .then(function(data) {
          if (data.success === true) {
            if (data.merchant) {
              self.merchant = data.merchant;
            }
            self.cardView = data.item;
            self.keepEmailTokenInRoute(data.item);
          } else {
            self.message = data.message;
            self.state = 2;
          }
        })
        .catch(function() {
          self.message = 'Could not register this gift card';
          self.state = 2;
        });
    },
    acceptInvite: function() {
      this.inviteError = '';
      if (!this.email || this.email.indexOf('@') < 0) {
        this.inviteError = 'Email is required';
        return;
      }
      let self = this;
      acceptGiftCardInvite({
        code: this.code,
        email: this.email.trim().toLowerCase(),
        name: this.name,
        phone: this.phone,
        message: this.inviteMessage,
      })
        .then(function(data) {
          if (data.success === true) {
            self.inviteAccepted = true;
            if (data.merchant) {
              self.merchant = data.merchant;
            }
            if (data.item) {
              self.cardView = data.item;
            }
            self.keepEmailTokenInRoute(data.item, '/giftcard/register');
          } else {
            self.inviteError = data.message || 'Could not accept this invitation';
          }
        })
        .catch(function() {
          self.inviteError = 'Could not accept this invitation';
        });
    },
    dateString: function(millis) {
      if (!millis) {
        return '';
      }
      return new Date(millis).toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'});
    }
  }
}
</script>

<style scoped>
.page-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  background: #f3f4f6;
  box-sizing: border-box;
}
.background-page {
  background: #f3f4f6;
  min-height: 100%;
}
.purchase-container.container,
.purchase-container {
  background: #f3f4f6;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}
.register-body,
.status-body {
  padding: 20px 0 8px 0;
  text-align: left;
}
@media (min-width: 600px) {
  .register-body,
  .status-body {
    padding-left: 18px;
    padding-right: 18px;
  }
}
.status-title,
.page-kicker {
  font-size: 22px;
  font-weight: 700;
  color: #111;
  margin-bottom: 14px;
}
.status-text,
.section-copy {
  font-size: 15px;
  color: #444;
  line-height: 1.5;
}
.section-copy {
  margin: 18px 0 8px 0;
}
.section-title {
  text-align: left;
  font-size: 20px;
  font-weight: 700;
  color: #111;
  margin: 28px 0 8px 0;
}
.virtual-card {
  background: linear-gradient(145deg, #0a2a52 0%, #031635 55%, #021024 100%);
  border-radius: 18px;
  min-height: 190px;
  padding: 22px 22px 20px 22px;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 8px 24px rgba(3, 22, 53, 0.28);
}
.virtual-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.virtual-card-merchant-block {
  min-width: 0;
  padding-right: 12px;
}
.virtual-card-merchant {
  font-size: 22px;
  font-weight: 500;
  line-height: 1.2;
}
.virtual-card-address {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
  opacity: 0.8;
}
.virtual-card-icon {
  width: 35px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
}
.virtual-card-label {
  font-size: 12px;
  letter-spacing: 0.08em;
  opacity: 0.9;
  font-weight: 600;
}
.virtual-card-amount {
  font-size: 36px;
  font-weight: 600;
  line-height: 1.15;
  margin-top: 2px;
}
.qr-panel {
  margin-top: 18px;
  background: #fff;
  border-radius: 16px;
  padding: 18px 16px 16px 16px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
}
.qr-panel img {
  width: 200px;
  max-width: 70%;
  border-radius: 10px;
  background: #fff;
}
.qr-hint {
  margin-top: 10px;
  font-size: 13px;
  color: #555;
}
.qr-code-text {
  margin-top: 4px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #031635;
}
.form-block {
  text-align: left;
  padding: 8px 0;
  font-size: 14px;
  color: #333;
}
.form-block .form-control {
  margin-top: 6px;
  border-radius: 12px;
  min-height: 48px;
}
.gift-message {
  resize: vertical;
  min-height: 80px;
}
.mandatory-text {
  color: #E84725;
}
.optional-text {
  color: #888;
  font-size: 13px;
}
.primary-btn {
  margin-top: 16px;
  width: 100%;
  border: none;
  background: #031635;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  border-radius: 12px;
  padding: 16px 18px;
  cursor: pointer;
}
.wallet-btn {
  margin-top: 12px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #000;
  color: #fff;
  padding: 14px 28px;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 700;
  font-size: 16px;
}
.registered-to {
  margin-top: 18px;
  text-align: center;
  font-size: 14px;
  color: #555;
}
.share-block {
  margin-top: 24px;
}
.status-note,
.form-error {
  margin-top: 10px;
  color: #c0392b;
  font-size: 14px;
}
.expiry-note {
  margin-top: 18px;
  text-align: center;
  font-size: 13px;
  color: #666;
}
.page-footer {
  margin-top: auto;
  padding: 28px 15px 36px 15px;
  border-top: 1px solid #eee;
  text-align: center;
}
.footer-merchant {
  margin-top: 0;
  font-size: 13px;
  color: #333;
  line-height: 1.45;
}
.privacy-note {
  margin-top: 14px;
  font-size: 12px;
  color: #666;
  line-height: 1.5;
}
.powered-by {
  margin-top: 12px;
  font-size: 12px;
  color: #666;
  line-height: 1.5;
}
</style>



// WEBPACK FOOTER //
// src/components/GiftCardRegister.vue